/**
 * categoryImages — Maps standard categories to a gradient + icon + accent color.
 * Used for the CompactGoalCard, HomeGoalsPreview, and GoalDetailPage hero.
 * No image generation — pure CSS gradients keep bundle small and load instant.
 */
import {
  BookOpen,
  DollarSign,
  Heart,
  Brain,
  Dumbbell,
  Target,
  Briefcase,
  Users,
  Plane,
  Laptop,
  Sparkles,
  Smile,
  ListChecks,
  Palette,
  Mountain,
  type LucideIcon,
} from "lucide-react";

export interface CategoryVisual {
  icon: LucideIcon;
  /** Tailwind gradient classes for the card background band */
  gradient: string;
  /** Tailwind classes for the icon chip background */
  chip: string;
  /** Tailwind text color for the icon inside the chip */
  iconColor: string;
}

const DEFAULT: CategoryVisual = {
  icon: Target,
  gradient: "from-slate-100 to-slate-50 dark:from-slate-900/40 dark:to-slate-900/10",
  chip: "bg-slate-200 dark:bg-slate-800",
  iconColor: "text-slate-700 dark:text-slate-200",
};

const MAP: Record<string, CategoryVisual> = {
  finance: {
    icon: DollarSign,
    gradient: "from-emerald-100 to-teal-50 dark:from-emerald-900/40 dark:to-teal-900/10",
    chip: "bg-emerald-200 dark:bg-emerald-800",
    iconColor: "text-emerald-700 dark:text-emerald-200",
  },
  education: {
    icon: BookOpen,
    gradient: "from-blue-100 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-900/10",
    chip: "bg-blue-200 dark:bg-blue-800",
    iconColor: "text-blue-700 dark:text-blue-200",
  },
  health: {
    icon: Heart,
    gradient: "from-rose-100 to-pink-50 dark:from-rose-900/40 dark:to-pink-900/10",
    chip: "bg-rose-200 dark:bg-rose-800",
    iconColor: "text-rose-700 dark:text-rose-200",
  },
  career: {
    icon: Briefcase,
    gradient: "from-amber-100 to-orange-50 dark:from-amber-900/40 dark:to-orange-900/10",
    chip: "bg-amber-200 dark:bg-amber-800",
    iconColor: "text-amber-700 dark:text-amber-200",
  },
  personal: {
    icon: Smile,
    gradient: "from-violet-100 to-purple-50 dark:from-violet-900/40 dark:to-purple-900/10",
    chip: "bg-violet-200 dark:bg-violet-800",
    iconColor: "text-violet-700 dark:text-violet-200",
  },
  family: {
    icon: Users,
    gradient: "from-orange-100 to-rose-50 dark:from-orange-900/40 dark:to-rose-900/10",
    chip: "bg-orange-200 dark:bg-orange-800",
    iconColor: "text-orange-700 dark:text-orange-200",
  },
  travel: {
    icon: Plane,
    gradient: "from-sky-100 to-cyan-50 dark:from-sky-900/40 dark:to-cyan-900/10",
    chip: "bg-sky-200 dark:bg-sky-800",
    iconColor: "text-sky-700 dark:text-sky-200",
  },
  fitness: {
    icon: Dumbbell,
    gradient: "from-red-100 to-orange-50 dark:from-red-900/40 dark:to-orange-900/10",
    chip: "bg-red-200 dark:bg-red-800",
    iconColor: "text-red-700 dark:text-red-200",
  },
  technology: {
    icon: Laptop,
    gradient: "from-slate-100 to-blue-50 dark:from-slate-900/40 dark:to-blue-900/10",
    chip: "bg-slate-200 dark:bg-slate-800",
    iconColor: "text-slate-700 dark:text-slate-200",
  },
  lifestyle: {
    icon: Sparkles,
    gradient: "from-fuchsia-100 to-pink-50 dark:from-fuchsia-900/40 dark:to-pink-900/10",
    chip: "bg-fuchsia-200 dark:bg-fuchsia-800",
    iconColor: "text-fuchsia-700 dark:text-fuchsia-200",
  },
  "mental health": {
    icon: Brain,
    gradient: "from-teal-100 to-emerald-50 dark:from-teal-900/40 dark:to-emerald-900/10",
    chip: "bg-teal-200 dark:bg-teal-800",
    iconColor: "text-teal-700 dark:text-teal-200",
  },
  productivity: {
    icon: ListChecks,
    gradient: "from-indigo-100 to-blue-50 dark:from-indigo-900/40 dark:to-blue-900/10",
    chip: "bg-indigo-200 dark:bg-indigo-800",
    iconColor: "text-indigo-700 dark:text-indigo-200",
  },
  creativity: {
    icon: Palette,
    gradient: "from-purple-100 to-fuchsia-50 dark:from-purple-900/40 dark:to-fuchsia-900/10",
    chip: "bg-purple-200 dark:bg-purple-800",
    iconColor: "text-purple-700 dark:text-purple-200",
  },
  "bucket list": {
    icon: Mountain,
    gradient: "from-pink-100 via-purple-50 to-indigo-50 dark:from-pink-900/40 dark:via-purple-900/30 dark:to-indigo-900/10",
    chip: "bg-gradient-to-br from-pink-300 to-purple-300 dark:from-pink-700 dark:to-purple-700",
    iconColor: "text-purple-800 dark:text-purple-100",
  },
};

export function getCategoryVisual(category?: string | null): CategoryVisual {
  if (!category) return DEFAULT;
  return MAP[category.toLowerCase()] ?? DEFAULT;
}
